import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Delete, ChevronLeft, ScanFace } from "lucide-react";
import { startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { api, errMsg, tokenStore } from "../lib/api";
import { CrownLogo, SunflowerLogo } from "../components/Logos";

const LETTERS = { 2: "ABC", 3: "DEF", 4: "GHI", 5: "JKL", 6: "MNO", 7: "PQRS", 8: "TUV", 9: "WXYZ" };
const done = (token) => { sessionStorage.removeItem("oh_aside"); tokenStore.set(token); window.location.replace("/home"); };

export async function passkeyLogin(user) {
  const opts = (await api.post("/auth/passkey/login/options", { user_id: user })).data;
  const cred = await startAuthentication({ optionsJSON: opts });
  return (await api.post("/auth/passkey/login/verify", { user_id: user, credential: cred })).data.token;
}

const hasPasskey = (u) => localStorage.getItem(`oh_passkey_${u}`) === "1" && browserSupportsWebAuthn();
async function faceLogin(user) {
  try { done(await passkeyLogin(user)); return ""; } catch (e) {
    if (e?.response?.status === 404) localStorage.removeItem(`oh_passkey_${user}`);
    return e?.name === "NotAllowedError" ? "Face ID canceled. Enter your PIN." : e?.response ? errMsg(e) : "Face ID unavailable. Enter your PIN.";
  }
}

function PinPad({ user, onBack, pinLength, initialMsg }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [faceMsg, setFaceMsg] = useState("");
  useEffect(() => { setFaceMsg(initialMsg || ""); }, [initialMsg]);
  const len = pinLength || 0;
  const hasKey = hasPasskey(user);
  const tryFace = async () => { setFaceMsg(""); setFaceMsg(await faceLogin(user)); };
  const submit = async (p) => {
    setBusy(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const r = await api.post("/auth/login", { user_id: user, pin: p, tz });
      done(r.data.token);
    } catch (e) { setErr(errMsg(e)); setPin(""); setBusy(false); }
  };
  const press = (d) => {
    if (busy) return;
    setErr("");
    const n = (pin + d).slice(0, 8);
    setPin(n);
    if (len && n.length === len) setTimeout(() => submit(n), 120);
  };
  return (
    <div className="flex flex-col items-center fade-in w-full max-w-xs" data-testid="pin-screen">
      <button className="icon-btn !text-white self-start mb-2" onClick={onBack} data-testid="pin-back-button" aria-label="Back"><ChevronLeft size={22} /></button>
      {user === "alexis" ? <CrownLogo size={56} /> : <SunflowerLogo size={56} />}
      <p className="text-[15px] text-white/90 mt-4">Enter Passcode</p>
      <div className={`flex gap-4 my-6 ${err ? "shake" : ""}`} data-testid="pin-pad-input">
        {Array.from({ length: Math.max(len || 4, pin.length) }).map((_, i) => (
          <span key={i} className={`pin-dot ${i < pin.length ? "pin-dot-on" : ""}`} />
        ))}
      </div>
      <p className="h-5 text-xs text-red-400 mb-2 text-center" data-testid="pin-error">{err || faceMsg}</p>
      <div className="grid grid-cols-3 gap-x-6 gap-y-4">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
          <button key={k} className="pin-key" disabled={busy} onClick={() => press(k)} data-testid={`pin-key-${k}`}>
            <span className="text-[2rem] leading-none font-light">{k}</span><span className="text-[9px] tracking-[0.2em] font-semibold h-3">{LETTERS[k] || ""}</span>
          </button>))}
        {hasKey ? <button className="pin-key pin-key-plain" onClick={tryFace} data-testid="pin-faceid-button" aria-label="Use Face ID"><ScanFace size={26} /></button> : <span />}
        <button className="pin-key" disabled={busy} onClick={() => press("0")} data-testid="pin-key-0"><span className="text-[2rem] leading-none font-light">0</span></button>
        <button className="pin-key pin-key-plain" disabled={busy} onClick={() => (pin.length >= 4 && pin.length !== len ? submit(pin) : setPin(pin.slice(0, -1)))} data-testid="pin-key-back" aria-label={pin.length >= 4 && pin.length !== len ? "Submit" : "Delete"}>
          {pin.length >= 4 && pin.length !== len ? <span className="text-sm" data-testid="pin-key-ok">OK</span> : <Delete size={22} />}
        </button>
      </div>
    </div>
  );
}

export default function Landing() {
  const [user, setUser] = useState(null);
  const [msg, setMsg] = useState("");
  const [lens, setLens] = useState({});
  const nav = useNavigate();
  const pick = async (u) => { setMsg(""); setUser(u); if (hasPasskey(u)) setMsg(await faceLogin(u)); };
  useEffect(() => {
    const prev = document.body.style.background;
    document.documentElement.style.background = "#000";
    document.body.style.background = "#000";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#000000");
    api.get("/auth/accounts").then((r) => setLens(Object.fromEntries(r.data.map((a) => [a.id, a.pin_length])))).catch(() => {});
    return () => { document.documentElement.style.background = ""; document.body.style.background = prev; };
  }, []);
  return (
    <div className="landing" data-testid="landing-screen">
      {user ? <PinPad user={user} pinLength={lens[user]} initialMsg={msg} onBack={() => setUser(null)} /> : (
        <div className="flex flex-col items-center rise">
          <p className="noice mb-8" data-testid="landing-noice-text">noice</p>
          <div className="flex items-center gap-10">
            <button className="landing-logo" onClick={() => pick("alexis")} data-testid="landing-logo-alexis" aria-label="Alexis"><CrownLogo size={84} /></button>
            <button className="landing-logo" onClick={() => pick("emily")} data-testid="landing-logo-emily" aria-label="Emily"><SunflowerLogo size={84} /></button>
          </div>
          <p className="noice noice-alt mt-8" data-testid="landing-toit-text">toit</p>
        </div>
      )}
      {!user && <p className="landing-est" role="link" onClick={() => nav("/creator")} data-testid="landing-est-text">EST. 10.13</p>}
    </div>
  );
}
